import { useEffect, useState } from 'react';
import { Fuel, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { api } from '../lib/api';
import { OverallStars } from './ScorecardFormDialog';

const rateClass = (r) => (r >= 30 ? 'text-red-700 font-bold' : r >= 15 ? 'text-amber-700 font-semibold' : 'text-slate-600');

const FlaggedList = ({ flagged }) => (
  <ul className="mt-2 space-y-1 text-xs font-data text-slate-600" data-testid="driver-flagged-list">
    {flagged.map((f) => (
      <li key={f.cost_id}>
        {f.date} · {f.vehicle_name} · {f.efficiency} {f.unit}
        <span className="text-red-700"> ({Math.abs(f.vs_avg_pct)}% {f.unit === 'L/hr' ? 'above' : 'below'} avg)</span>
        {f.liters ? ` · ${f.liters} L` : ''}{f.invoice_number ? ` · ${f.invoice_number}` : ''}
      </li>
    ))}
  </ul>
);

const RankingRow = ({ row, rank }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3 border-b border-slate-100 last:border-0" data-testid={`fuel-ranking-row-${row.driver_id}`}>
      <div className="flex items-center gap-4">
        <span className="w-6 font-data text-slate-400">{rank}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">{row.driver_name}</p>
          <p className="text-xs text-slate-500 truncate">{row.vehicles.join(', ')}</p>
        </div>
        <div className="w-36" data-testid={`fuel-ranking-score-${row.driver_id}`}>
          {row.score_count > 0 ? (
            <>
              <OverallStars value={row.score_avg} size="w-3.5 h-3.5" />
              <p className="text-[11px] text-slate-400">{row.score_count} scorecard{row.score_count === 1 ? '' : 's'}</p>
            </>
          ) : <p className="text-xs text-slate-400">Not scored</p>}
        </div>
        <div className="text-right w-24">
          <p className="font-data font-semibold text-slate-900" data-testid={`fuel-ranking-alerts-${row.driver_id}`}>
            {row.alerts} / {row.fills}
          </p>
          <p className="text-[11px] text-slate-400">flagged fills</p>
        </div>
        <div className="text-right w-20">
          <p className={`font-data ${rateClass(row.alert_rate)}`}>{row.alert_rate}%</p>
          <p className="text-[11px] text-slate-400">rate</p>
        </div>
        <div className="text-right w-24">
          <p className="font-data text-slate-700">{row.worst_pct ? `${row.worst_pct}%` : '-'}</p>
          <p className="text-[11px] text-slate-400">worst</p>
        </div>
        <button
          type="button"
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30"
          onClick={() => setOpen((o) => !o)}
          disabled={!row.flagged.length}
          aria-label="Show flagged fill-ups"
          data-testid={`fuel-ranking-toggle-${row.driver_id}`}
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && <div className="pl-10"><FlaggedList flagged={row.flagged} /></div>}
    </div>
  );
};

export const DriverFuelRanking = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.getDriverFuelRanking().then(setData).catch(() => setData(null));
  }, []);
  if (!data || !data.drivers.length) return null;
  return (
    <Card className="border-slate-200" data-testid="driver-fuel-ranking">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
              <Fuel className="w-4 h-4 text-red-600" /> Driver Fuel Ranking
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Fill-ups more than 20% less efficient than the vehicle's average, counted against each driver's currently assigned vehicle(s).
              {data.unassigned_drivers > 0 && ` ${data.unassigned_drivers} driver(s) without a vehicle are not ranked.`}
            </p>
          </div>
        </div>
        {data.drivers.map((row, i) => <RankingRow key={row.driver_id} row={row} rank={i + 1} />)}
      </CardContent>
    </Card>
  );
};
